<template>
    <div class="appeal-edit-vue org_settings ">
        <div class="row px-2 mb-5">
            <div class="wizard-header">
                <div class="wizard-progress">
                    <div class="progress-steps">
                        <div class="progress-bar-container">
                            <div class="progress-bar">
                                <div class="progress" :style="{ width: `${progressPercentage}%` }"></div>
                            </div>
                        </div>
                        <div 
                            v-for="(step, index) in steps" 
                            :key="`step-${index+1}`" 
                            class="step"  
                            @click="canNavigateToStep(index+1) ? goToStep(index+1) : null"
                            :class="{ 
                                'active': active_step >= index+1, 
                                'completed': active_step > index+1,
                                'disabled': !canNavigateToStep(index+1)
                            }"
                            :style="{ cursor: canNavigateToStep(index+1) ? 'pointer' : 'not-allowed' }"
                        >
                            <div class="step-number">
                                <template v-if="active_step > index">
                                    <i class="fas fa-check m-0"></i>
                                </template>
                            </div>
                            <div class="step-label">{{ step.title }}</div>
                        </div>
                    </div>
                </div>
            </div>
    
            <div class="wizard-content">
                <div class="row justify-content-center">
                    <div class="col-10 p-4">
                        <!-- Appeal Information -->
                        <div class="row step-1" v-if="active_step === 1">
                            <h3 class="step-title">Step 1: Mailing Information</h3>
                            <div class="col-12 card">
                                <div class="card-body">
                                    <div class="row">
                                        <div class="col-sm-9 mb-3">
                                            <label class="form-label" for="local_appeal.name">Name</label>
                                            <input type="text" class="form-control" id="local_appeal.name" v-model="local_appeal.name" :class="{ 'is-invalid': errors.name }" maxlength="256"
                                                :disabled="!canEdit('direct_mail')" aria-label="Name">
                                            <div class="invalid-feedback" v-if="errors.name">
                                                {{ errors.name }}
                                            </div>
                                        </div>
                                        <div class="col-sm-3 mb-3">
                                            <label class="form-label" for="local_appeal.drop_date">Drop Date <span class="text-danger">*</span></label>
                                            <datepicker-utc 
                                                v-model="local_appeal.drop_date"
                                                :input-class="errors.drop_date ? 'form-control date is-invalid' : 'form-control date'"
                                                position="right"
                                                :disabled="!canEdit('direct_mail')"
                                            />
                                            <div class="invalid-feedback" v-if="errors.drop_date">
                                                {{ errors.drop_date }}
                                            </div>
                                        </div>
                                    </div>
                                    <div class="row">
                                        <div class="col-sm-6 mb-3">
                                            <label class="form-label" for="local_appeal.appeal_code">Default Appeal Code 
                                                <img class="info-icon" src="/img/icons/avid-icons/info.svg" data-bs-toggle="tooltip" title="This can be overwritten for each package." alt="" />
                                            </label>
                                            <input type="text" class="form-control" id="local_appeal.appeal_code" v-model="local_appeal.appeal_code" maxlength="64" :disabled="!canEdit('direct_mail')" aria-label="Appeal Code">
                                        </div>
                                        <div class="col-sm-6">
                                            <div class="mb-0">
                                                <label class="form-label" for="appeal-finder-number">Starting Finder Number</label>
                                                <input
                                                    type="text"
                                                    class="form-control"
                                                    id="appeal-finder-number"
                                                    v-model="local_appeal.finder_number"
                                                    placeholder="Optional"
                                                    @keypress="isNumber($event)"
                                                    @paste="stripNonNumbers($event)"
                                                    :disabled="!canEdit('direct_mail')"
                                                 aria-label="Starting Finder Number">
                                            </div>
                                        </div>
                                    </div>

                                    <div class="row g-3 mt-0">
                                        <div class="col-sm-6">
                                            <div class="mb-0">
                                                <label class="form-label" for="appeal-total-budget">Total Budget</label>
                                                <div class="input-group">
                                                    <span class="input-group-text">$</span>
                                                    <input
                                                        type="text"
                                                        class="form-control"
                                                        id="appeal-total-budget"
                                                        v-model="local_appeal.budget"
                                                        placeholder="0.00"
                                                        @keypress="isNumber($event)"
                                                        @paste="stripNonNumbers($event)"
                                                        :disabled="!canEdit('direct_mail')" aria-label="Text input">
                                                </div>
                                            </div>
                                        </div>
                                        <div class="col-sm-6">
                                            <div class="mb-0">
                                                <label class="form-label" for="appeal-fixed-project-cost">
                                                    Fixed Project Costs
                                                    <img class="info-icon" src="/img/icons/avid-icons/info.svg" data-bs-toggle="tooltip" title="This includes project costs such as copy writing, design, and agency fees." alt="" />
                                                </label>
                                                <div class="input-group">
                                                    <span class="input-group-text">$</span>
                                                    <input
                                                        type="text"
                                                        class="form-control"
                                                        id="appeal-fixed-project-cost"
                                                        v-model="local_appeal.fixed_project_cost"
                                                        placeholder="0.00"
                                                        @keypress="isNumber($event)"
                                                        @paste="stripNonNumbers($event)"
                                                        :disabled="!canEdit('direct_mail')" aria-label="Text input">
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div class="mt-4">
                                        <h4 class="mb-2">Selection Limitations</h4>
                                        <div class="row g-2">
                                            <div class="col-sm-7 col-12">
                                                <div class="form-check form-switch d-inline-flex align-items-center gap-2">
                                                    <input
                                                        class="form-check-input"
                                                        type="checkbox"
                                                        id="appeal-one-record-per-household"
                                                        v-model="local_appeal.one_record_per_household"
                                                        :disabled="!canEdit('direct_mail')"
                                                     aria-label="Checkbox">
                                                    <label class="form-check-label" for="appeal-one-record-per-household">Select only one record per household</label>
                                                </div>
                                            </div>
                                            <div class="col-sm-7 col-12">
                                                <div class="form-check form-switch d-inline-flex align-items-center gap-2">
                                                    <input
                                                        class="form-check-input"
                                                        type="checkbox"
                                                        id="appeal-primary-addresses-only"
                                                        v-model="local_appeal.primary_addresses_only"
                                                        :disabled="!canEdit('direct_mail')"
                                                     aria-label="Checkbox">
                                                    <label class="form-check-label" for="appeal-primary-addresses-only">Select primary addresses only</label>
                                                </div>
                                            </div>
                                            <div class="col-sm-7 col-12">
                                                <div class="form-check form-switch d-inline-flex align-items-center gap-2">
                                                    <input
                                                        class="form-check-input"
                                                        type="checkbox"
                                                        id="appeal-exclude-deceased"
                                                        v-model="local_appeal.exclude_deceased"
                                                        :disabled="!canEdit('direct_mail')"
                                                     aria-label="Checkbox">
                                                    <label class="form-check-label" for="appeal-exclude-deceased">Exclude deceased</label>
                                                </div>
                                            </div>
                                            <div class="col-sm-7 col-12">
                                                <div class="form-check form-switch d-inline-flex align-items-center gap-2">
                                                    <input
                                                        class="form-check-input"
                                                        type="checkbox"
                                                        id="appeal-file-dedupe"
                                                        v-model="file_dedupe"
                                                        @change="toggleDedupe()"
                                                        :disabled="!canEdit('direct_mail')"
                                                     aria-label="Checkbox">
                                                    <label class="form-check-label" for="appeal-file-dedupe">De-duplicate records based upon address</label>
                                                </div>
                                            </div>
                                            <div class="col-sm-7 col-12" v-if="file_dedupe">
                                                <label class="form-label mt-2" for="appeal-dedupe-priority">De-dupe Priority</label>
                                                <select
                                                    class="form-select"
                                                    id="appeal-dedupe-priority"
                                                    v-model="local_appeal.dedupe_priority"
                                                    :disabled="!canEdit('direct_mail')"
                                                 aria-label="De-dupe Priority">
                                                    <option value="giving_history.largest_gift.amount">Prioritize based upon Largest Giving Amount</option>
                                                    <option value="giving_history.last_gift.date">Prioritize based upon Last Giving Date</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>

                                    <div class="mt-4">
                                        <h4 class="mb-2">Segment Selection Axes</h4>
                                        <div class="row g-3">
                                            <div class="col-sm-4">
                                                <label class="form-label" for="appeal-x-axis">X Axis</label>
                                                <select
                                                    class="form-select"
                                                    id="appeal-x-axis"
                                                    v-model="local_appeal.x_axis"
                                                    @change="updateAxisList()"
                                                    :class="{ 'is-invalid': errors.x_axis }"
                                                    :disabled="!canEdit('direct_mail')"
                                                 aria-label="X Axis">
                                                    <option value="">Select X Axis</option>
                                                    <template v-for="axis in axis_list" :key="axis.value">
                                                        <option :value="axis.value" v-if="!isAxisSelected(axis.value) || local_appeal.x_axis === axis.value">
                                                            {{ axis.name }}
                                                        </option>
                                                    </template>
                                                </select>
                                                <div class="invalid-feedback" v-if="errors.x_axis">
                                                    {{ errors.x_axis }}
                                                </div>
                                            </div>
                                            <div class="col-sm-4">
                                                <label class="form-label" for="appeal-y-axis">Y Axis</label>
                                                <select
                                                    class="form-select"
                                                    id="appeal-y-axis"
                                                    v-model="local_appeal.y_axis"
                                                    @change="updateAxisList()"
                                                    :class="{ 'is-invalid': errors.y_axis }"
                                                    :disabled="!canEdit('direct_mail')"
                                                 aria-label="Y Axis">
                                                    <option value="">Select Y Axis</option>
                                                    <template v-for="axis in axis_list" :key="axis.value">
                                                        <option :value="axis.value" v-if="!isAxisSelected(axis.value) || local_appeal.y_axis === axis.value">
                                                            {{ axis.name }}
                                                        </option>
                                                    </template>
                                                </select>
                                                <div class="invalid-feedback" v-if="errors.y_axis">
                                                    {{ errors.y_axis }}
                                                </div>
                                            </div>
                                            <div class="col-sm-4">
                                                <label class="form-label" for="appeal-z-axis">Z Axis</label>
                                                <select
                                                    class="form-select"
                                                    id="appeal-z-axis"
                                                    v-model="local_appeal.z_axis"
                                                    @change="updateAxisList()"
                                                    :class="{ 'is-invalid': errors.z_axis }"
                                                    :disabled="!canEdit('direct_mail')"
                                                 aria-label="Z Axis">
                                                    <option value="">Select Z Axis</option>
                                                    <template v-for="axis in axis_list" :key="axis.value">
                                                        <option :value="axis.value" v-if="!isAxisSelected(axis.value) || local_appeal.z_axis === axis.value">
                                                            {{ axis.name }}
                                                        </option>
                                                    </template>
                                                </select>
                                                <div class="invalid-feedback" v-if="errors.z_axis">
                                                    {{ errors.z_axis }}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div class="mt-4">
                                        <button class="btn btn-primary" @click="saveAppeal(true)" :disabled="actions.saving">
                                            <span v-if="actions.saving"><i class="fas fa-spinner fa-spin"></i> Saving</span>
                                            <span v-else>Next Step</span>
                                        </button>

                                        <a class="btn btn-secondary ms-2" :href="'/direct_mail/' + client.url + '/appeals'" >
                                            Cancel
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
    
                        <!-- Segments -->
                        <div class="row step-2" v-if="active_step == 2">
                            <h3 class="step-title">Step 2: Define the Audience</h3>
                            <p class="col-12">Drag segments from the left into Includes, Exclusions, or Seeds on the right.</p>
                            <div class="col-12 col-lg-6 mb-4 mb-lg-0">
                                <div id="appeal-choose-audience" class="card h-100">
                                    <div class="d-flex justify-content-between align-items-center mb-3">
                                        <h3 class="mb-0">Available Audiences</h3>
                                        <button type="button" class="btn btn-primary btn-sm px-3" v-if="canEdit('direct_mail') || canEdit('pathways')" @click="openDescribeSegmentModal">
                                            <img class="icon" src="/img/icons/avid-icons/edna.svg" alt=""> Add Audience
                                        </button>
                                    </div>
                                    <div class="position-relative mb-3">
                                        <input type="text" class="search-box form-control me-1 py-2" placeholder="Search segments..." aria-label="Search segments..." v-model="segment_search" @input="handleSegmentSearch()">
                                        <i class="fa fa-search search-placeholder-icon" v-if="segment_search == ''"></i>
                                    </div>
                                    <div class="flex-grow-1 d-flex flex-column">
                                        <div v-if="filtered_segments.length === 0" class="card long-card disabled mb-3">
                                            <div class="card-body long-card__body row">
                                                <div class="long-card__content">
                                                    <h4 class="mb-1">No Audiences Found</h4>
                                                </div>
                                            </div>
                                        </div>
                                        <draggable
                                            v-else
                                            tag="div"
                                            class="d-flex flex-column appeal-choose-list flex-grow-1"
                                            v-model="available_segments"
                                            handle=".appeal-drag-handle"
                                            :sort="false"
                                            :group="{ name: 'a', pull: true, put: true }"
                                            @change="handleDragChange"
                                            @start="onAvailableSegmentDragStart"
                                            @end="onAvailableSegmentDragEnd"
                                            :ghostClass="'dragging-ghost'"
                                            :animation="150"
                                            item-key="id"
                                            :disabled="!canEdit('direct_mail')"
                                        >
                                            <template #item="{ element: segment }">
                                                <div class="card long-card mb-3 appeal-choose-card" :data-id="segment.id" @click="canEdit('direct_mail') && addSegmentToFirstAvailable(segment)">
                                                    <a v-if="canEdit('direct_mail') || canEdit('pathways')" :href="'/audience/' + client.url + '/' + segment.id" class="btn btn-white btn-icon-only-sm appeal-choose-edit-btn" aria-label="Edit audience" data-bs-toggle="tooltip" title="Edit" @click.stop>
                                                        <img class="icon" src="/img/icons/avid-icons/edit.svg" alt="">
                                                    </a>
                                                    <div class="card-body long-card__body row">
                                                        <div class="long-card__content">
                                                            <div class="d-flex align-items-center position-relative" style="gap: 15px;">
                                                                <div class="long-card-action appeal-drag-handle" v-if="canEdit('direct_mail')">
                                                                    <div class="btn btn-white btn-icon-only-sm">
                                                                        <img src="/img/icons/avid-icons/drag-n-drop.svg" alt="drag" class="icon">
                                                                    </div>
                                                                </div>
                                                                <div style="flex: 1 1 0; min-width:0;">
                                                                    <h4 class="mb-0">{{ segment.name }}</h4>
                                                                    <div class="audience-info-grid">
                                                                        <div class="info-item">
                                                                            <div class="info-label">Contacts</div>
                                                                            <div class="info-value">{{ segment.sizes ? number_with_zero(getSize(segment)) : 0 }}</div>
                                                                        </div>
                                                                        <div class="info-item">
                                                                            <div class="info-label">Source</div>
                                                                            <div class="info-value">{{ segment.data_source?.name || 'N/A' }}</div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </template>
                                        </draggable>
                                        <div class="table-pagination mt-3 justify-content-center justify-content-md-between">
                                            <div class="table-pagination--group d-none d-md-flex">
                                                <div class="position-relative">
                                                    <select class="form-select" name="num_per_page" id="num_per_page" v-model="num_per_page" @change="reloadPagination(filtered_segments)" aria-label="Num Per Page">
                                                        <option value="5000">ALL</option>
                                                        <option v-for="num in num_per_page_options" :key="num" :value="num">{{ num }}</option>
                                                    </select>
                                                    <div class="select-display-text">Showing {{ num_per_page === '5000' ? 'ALL' : num_per_page }}</div>
                                                </div>
                                            </div>
                                            <div class="table-pagination--group d-none d-lg-flex">{{ filtered_segments.length === 0 ? '0' : item_interval }} of {{ filtered_segments.length }}</div>
                                            <div class="table-pagination--group justify-content-center justify-content-md-end">
                                                <button v-if="page_count>1" class="btn btn-secondary btn-icon-only-md me-1" @click="moveToPrevPage(filtered_segments)" :disabled="page_index == 0" data-bs-toggle="tooltip" title="Previous page">
                                                    <img class="icon" src="/img/icons/avid-icons/chevron-left.svg" alt="Previous">
                                                </button>
                                                <button v-if="page_count>1" class="btn btn-secondary btn-icon-only-md ms-1" @click="moveToNextPage(filtered_segments)" :disabled="page_index == page_count -1" data-bs-toggle="tooltip" title="Next page">
                                                    <img class="icon" src="/img/icons/avid-icons/chevron-right.svg" alt="Next">
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="d-flex justify-content-start pt-3">
                                        <button class="btn btn-primary" @click="saveAppealSegments()" :disabled="actions.saving || !active_segments.targets.length">
                                            <span v-if="actions.saving"><i class="fas fa-spinner fa-spin"></i> Processing</span>
                                            <span v-else>Next Step</span>
                                        </button>
                                        <a class="btn btn-secondary ms-2" :href="'/direct_mail/' + client.url + '/appeals'">Cancel</a>
                                    </div>
                                </div>
                            </div>
                            <div class="col-12 col-lg-6 d-flex flex-column">
                                <nav class="navbar navbar-expand primary-tab-bar appeal-segment-tab-bar mb-3">
                                    <div class="container-fluid p-0 w-100">
                                        <div class="collapse navbar-collapse">
                                            <ul class="navbar-nav w-100 mb-lg-0">
                                                <li class="nav-item appeal-segment-tab" v-for="tab in segmentTabOptions" :key="tab.key">
                                                    <a class="nav-link d-flex flex-nowrap primary-tab"
                                                       :class="{ 'primary-tab-active': activeSegmentTab === tab.key }"
                                                       href="#"
                                                       role="button"
                                                       @click.prevent="activeSegmentTab = tab.key">
                                                        <span>{{ tab.label }} ({{ active_segments[tab.key].length }})</span>
                                                    </a>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </nav>
                                <div id="appeal-selected-audience" class="card flex-grow-1 appeal-audience-column gap-0">
                                    <div class="flex-grow-1 overflow-auto px-3 pt-3">
                                        <p class="mb-3">{{ segmentTabDescription }}</p>
                                        <div class="appeal-audience-dropzone-wrap" :class="{ 'segment-drag-in-progress': segmentDragInProgress }">
                                            <draggable
                                                v-model="active_segments[activeSegmentTab]"
                                                tag="div"
                                                class="d-flex flex-column appeal-audience-dropzone-list"
                                                handle=".handle"
                                                :sort="false"
                                                :group="{ name: 'a', pull: true, put: true }"
                                                @change="handleActiveSegmentDragChange"
                                                :ghostClass="'dragging-ghost'"
                                                :animation="150"
                                                item-key="id"
                                                :disabled="!canEdit('direct_mail')"
                                            >
                                                <template #item="{ element: segment }">
                                                    <div class="card long-card me-1 mb-3" :data-id="segment.id">
                                                        <div class="card-body long-card__body row">
                                                            <div class="long-card__content">
                                                                <div class="d-flex align-items-center position-relative" style="gap: 15px;">
                                                                    <div style="flex: 1 1 0; min-width:0;">
                                                                        <h4 class="mb-0">{{ segment.name }}</h4>
                                                                        <div class="audience-info-grid">
                                                                            <div class="info-item">
                                                                                <div class="info-label">Contacts</div>
                                                                                <div class="info-value">{{ segment.sizes ? number_with_zero(getSize(segment)) : 0 }}</div>
                                                                            </div>
                                                                            <div class="info-item">
                                                                                <div class="info-label">Source</div>
                                                                                <div class="info-value">{{ segment.data_source?.name || 'N/A' }}</div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <div class="long-card-action appeal-remove-action" v-if="canEdit('direct_mail')">
                                                                        <button type="button" class="btn btn-none btn-icon-only-sm btn-audience-remove" aria-label="Remove" @click.stop="removeSegment(segment, activeSegmentTab)" data-bs-toggle="tooltip" title="Remove">
                                                                            <img class="icon" src="/img/icons/avid-icons/red-remove.svg" alt="">
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </template>
                                            </draggable>
                                            <div
                                                class="audience-dropzone mb-3"
                                                :class="{ 'audience-dropzone-empty': active_segments[activeSegmentTab].length === 0 && !segmentDragInProgress }"
                                            >
                                                <div class="d-flex flex-column align-items-center justify-content-center appeal-dropzone-inner">
                                                    <template v-if="active_segments[activeSegmentTab].length === 0 && !segmentDragInProgress">
                                                        <h4 class="mb-2">No {{ segmentTabLabel }} Selected</h4>
                                                    </template>
                                                    <div class="d-flex align-items-center">
                                                        <img src="/img/icons/avid-icons/drag-n-drop.svg" alt="drop" class="me-2">
                                                        <span class="mb-0 text-muted small">Drop a new audience above to set its place.</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
    
                        <!-- Packages -->
                        <div class="row step-3" v-if="active_step == 3">
                            <div class="col-12">
                                <h3 class="step-title">Step 3: Define the Packages</h3>
                                <div class="card mb-3 table-card">
                                    <div class="card-header">
                                        <div class="row">
                                            <div class="col-lg-6 col-12">
                                                <h3 class="mt-2">Packages ({{filtered_packages.length}})</h3>
                                            </div>
                                            <div class="col-lg-6 d-none d-lg-block">
                                                <div class="d-flex justify-content-end justify-self-end gap-2">
                                                    <div class="position-relative">
                                                        <input type="text" class="search-box form-control me-1 py-2" placeholder="Search packages..." aria-label="Search packages..." v-model="package_search" @input="handlePackageSearch()">
                                                        <i class="fa fa-search search-placeholder-icon" v-if="package_search == ''"></i>
                                                    </div>
    
                                                    <div v-if="!canEdit('direct_mail')" class="d-inline-block" data-bs-toggle="tooltip" title="You do not have privileges to complete this action">
                                                        <button class="btn btn-primary" :disabled="true">
                                                            <img class="icon" src="/img/icons/avid-icons/plus.svg" alt="">Add Package
                                                        </button>
                                                    </div>
                                                    <button v-else class="btn btn-primary" @click="openPackageModal('add')">
                                                        <img class="icon" src="/img/icons/avid-icons/plus.svg" alt="">Add Package
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
    
                                    <div class="card-body p-0">
                                        <table class="table table-responsive table-with-pagination m-0">
                                            <thead>
                                                <tr class="table-header-row">
                                                    <th @click="sortPackagesByKey('letter')" class="table-column-2">
                                                        <div class="d-flex flex-row">
                                                            Package
                                                            <div class="d-flex flex-column">
                                                                <img class="sort-key" src="/img/icons/avid-icons/chevron-up.svg" :class="(package_sort_key=='letter' && package_sort_order=='asc')? 'sort-key-active' :''" alt="">
                                                                <img class="sort-key" src="/img/icons/avid-icons/chevron-down.svg" :class="(package_sort_key=='letter' && package_sort_order=='des')? 'sort-key-active' :''" alt="">
                                                            </div>
                                                        </div>
                                                    </th>
                                                    <th @click="sortPackagesByKey('description')" class="table-column-1">
                                                        <div class="d-flex flex-row">
                                                            Description
                                                            <div class="d-flex flex-column">
                                                                <img class="sort-key" src="/img/icons/avid-icons/chevron-up.svg" :class="(package_sort_key=='description' && package_sort_order=='asc')? 'sort-key-active' :''" alt="">
                                                                <img class="sort-key" src="/img/icons/avid-icons/chevron-down.svg" :class="(package_sort_key=='description' && package_sort_order=='des')? 'sort-key-active' :''" alt="">
                                                            </div>
                                                        </div>
                                                    </th>
                                                    <th @click="sortPackagesByKey('carrier_postage')" class="table-column-2">
                                                        <div class="d-flex flex-row">
                                                            Carrier Postage
                                                            <div class="d-flex flex-column">
                                                                <img class="sort-key" src="/img/icons/avid-icons/chevron-up.svg" :class="(package_sort_key=='carrier_postage' && package_sort_order=='asc')? 'sort-key-active' :''" alt="">
                                                                <img class="sort-key" src="/img/icons/avid-icons/chevron-down.svg" :class="(package_sort_key=='carrier_postage' && package_sort_order=='des')? 'sort-key-active' :''" alt="">
                                                            </div>
                                                        </div>
                                                    </th>
                                                    <th @click="sortPackagesByKey('reply_postage')" class="table-column-2">
                                                        <div class="d-flex flex-row">
                                                            Reply Postage
                                                            <div class="d-flex flex-column">
                                                                <img class="sort-key" src="/img/icons/avid-icons/chevron-up.svg" :class="(package_sort_key=='reply_postage' && package_sort_order=='asc')? 'sort-key-active' :''" alt="">
                                                                <img class="sort-key" src="/img/icons/avid-icons/chevron-down.svg" :class="(package_sort_key=='reply_postage' && package_sort_order=='des')? 'sort-key-active' :''" alt="">
                                                            </div>
                                                        </div>
                                                    </th>
                                                    <th @click="sortPackagesByKey('carrier_size')" class="table-column-2">
                                                        <div class="d-flex flex-row">
                                                            Carrier Size
                                                            <div class="d-flex flex-column">
                                                                <img class="sort-key" src="/img/icons/avid-icons/chevron-up.svg" :class="(package_sort_key=='carrier_size' && package_sort_order=='asc')? 'sort-key-active' :''" alt="">
                                                                <img class="sort-key" src="/img/icons/avid-icons/chevron-down.svg" :class="(package_sort_key=='carrier_size' && package_sort_order=='des')? 'sort-key-active' :''" alt="">
                                                            </div>
                                                        </div>
                                                    </th>
                                                    <th @click="sortPackagesByKey('double_sided')" class="table-column-2">
                                                        <div class="d-flex flex-row">
                                                            Double Sided
                                                            <div class="d-flex flex-column">
                                                                <img class="sort-key" src="/img/icons/avid-icons/chevron-up.svg" :class="(package_sort_key=='double_sided' && package_sort_order=='asc')? 'sort-key-active' :''" alt="">
                                                                <img class="sort-key" src="/img/icons/avid-icons/chevron-down.svg" :class="(package_sort_key=='double_sided' && package_sort_order=='des')? 'sort-key-active' :''" alt="">
                                                            </div>
                                                        </div>
                                                    </th>
                                                    <th class="table-column-2 pe-3" width="1" v-if="canEdit('direct_mail')">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr v-for="(pack, index) in filtered_packages" :class="`table-row-${index+1}`">
                                                    <td class="align-middle" :class="getTextColorClass(pack.color)" :style="{backgroundColor: pack.color }">{{ pack.letter }}</td>
                                                    <td class="align-middle">
                                                        <a href="#" @click.prevent="editPackage(index)" title="Edit" v-if="canEdit('direct_mail')">{{ pack.description }}</a>
                                                        <span v-else>{{ pack.description }}</span>
                                                    </td>
                                                    <td class="align-middle">{{ postage(pack.carrier_postage) }}</td>
                                                    <td class="align-middle">{{ postage(pack.reply_postage) }}</td>
                                                    <td class="align-middle">{{ size(pack.carrier_size) }}</td>
                                                    <td class="align-middle">{{ readable_boolean(pack.double_sided) }}</td>
                                                    <td class="align-middle" v-if="canEdit('direct_mail')">
                                                        <div class="dropdown d-flex justify-content-center">
                                                            <button class="btn btn-white btn-icon-only-md" data-bs-toggle="dropdown" data-bs-auto-close="true" aria-expanded="false">
                                                                <img class="icon" src="/img/icons/avid-icons/dots-vertical.svg" alt="">
                                                            </button>
                                                            <div class="dropdown-menu dropdown-menu-primary dropdown-menu-end">
                                                                <a class="dropdown-item" href="#" @click.prevent="copyPackage(index)">
                                                                    <img class="icon me-2" src="/img/icons/avid-icons/document-copy.svg" alt=""> Copy Package
                                                                </a>
                                                                <a class="dropdown-item" href="#" @click.prevent="editPackage(index)">
                                                                    <img class="icon me-2" src="/img/icons/avid-icons/edit.svg" alt=""> Edit Package
                                                                </a>
                                                                
                                                                <hr class="my-1" v-if="!package.hasOwnProperty('index')">
                                                                <a class="dropdown-item text-danger" href="#" @click.prevent="removePackage(index)" v-if="!package.hasOwnProperty('index')">
                                                                    <img class="icon red-icon me-2" src="/img/icons/avid-icons/trash.svg" alt=""> Delete Package
                                                                </a>
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                                <tr v-if="!filtered_packages.length">
                                                    <td colspan="7" class="text-center  p-3">You have no packages defined. <a class="bg-white" href="#" @click.prevent="openPackageModal('add')" v-if="canEdit('direct_mail')">Add a Package</a></td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
    
                                    <div class="card-footer">
                                        <button v-if="actions.saving" class="btn btn-primary" disabled><i class="fas fa-spinner fa-spin"></i> Processing</button>
                                        <button v-else class="btn btn-primary" @click="nextStep()" :disabled="actions.saving || !packages.length">Next Step</button>
    
                                        <a class="btn btn-secondary ms-2" :href="'/direct_mail/' + client.url + '/appeals'" >
                                            Cancel
                                        </a>                                
                                    </div>
                                </div>
    
                                <package-modal
                                    :show="showPackageModal"
                                    :package="package"
                                    :direct_mail_id="local_appeal.id"
                                    @close="closePackageModal"
                                    @package-saved="handlePackageSaved"
                                    @package-updated="handlePackageUpdated"
                                />
                            </div>
                        </div>
    
                        <!-- Audiences -->
                        <div class="row step-4" v-if="active_step == 4">
                            <h3 class="step-title">Step 4: Audience to Package Selection</h3>
                            <p>Select a package by clicking on the associated color and select the segments to receive the mailing</p>
    
                            <div class="col-12 card">
                                <div class="card-body"> 
                                    <package-selection :packages="packages" :cube-data="cube" :dedupe="file_dedupe" :axis_list="axis_list" @next-step="nextStep()"></package-selection>                                
                                </div>
                            </div>
                        </div>
    
                        <!-- Finalize -->
                        <div class="row step-5" v-if="active_step == 5">
                            <h3 class="step-title">Step 5: Verify Your Selections</h3>
                            <div class="col-12 card">
                                <div class="card-body"> 
                                    <h4><strong>{{ local_appeal.name }}</strong>: <span class="text-muted">{{ local_appeal.appeal_code }}</span></h4>
    
                                    <h4>Audiences</h4>
    
                                    <div class="mb-4">
                                        <div class="border rounded p-0 mb-3">
                                            <div class="bg-light border-bottom px-3 py-2">
                                                <strong>Included</strong>
                                            </div>
                                            <div class="px-3 py-2">
                                                <ul class="list-unstyled mb-0">
                                                    <li v-for="(segment, index) in active_segments.targets" :class="{'border-bottom': index < active_segments.targets.length - 1}" class="py-1">
                                                        {{ segment.name }}
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>
    
                                        <div class="border rounded p-0" v-if="active_segments.exclusions.length">
                                            <div class="bg-light border-bottom px-3 py-2">
                                                <strong>Excluded</strong>
                                            </div>
                                            <div class="px-3 py-2">
                                                <ul class="list-unstyled mb-0">
                                                    <li v-for="(segment, index) in active_segments.exclusions" :class="{'border-bottom': index < active_segments.exclusions.length - 1}" class="py-1">
                                                        {{ segment.name }}
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
    
                                    <h4>Packages</h4>
                                    <div class="card mb-3 table-card">
                                        <div class="card-body p-0">
                                            <div class="table-responsive">
                                                <table class="table m-0 table-with-pagination">
                                                <thead>
                                                    <tr class="table-header-row">
                                                        <th class="table-column-2 ps-3">Appeal Code</th>
                                                        <th class="table-column-2">Package Code</th>
                                                        <th class="table-column-1">Description</th>
                                                        <th class="table-column-2 text-end">Price / Piece</th>
                                                        <th class="table-column-2 text-end">Audience Size</th>
                                                        <th class="table-column-2 text-end pe-3">Total Cost</th>
                                                    </tr>
                                                </thead>
                                                <tbody class="table-border-bottom-0">
                                                    <tr v-for="(pack, index) in packages">
                                                        <td v-if="pack.experiment == 0" class=" ps-3 py-2 border-bottom">{{ pack.appeal_code }}</td>
                                                        <td v-if="pack.experiment == 0" class="py-2 border-bottom">{{ pack.letter }}</td>
                                                        <td v-if="pack.experiment == 0" class="py-2 border-bottom">{{ pack.description }}</td>
                                                        <td v-if="pack.experiment == 0" class="text-end py-2 border-bottom">{{ currency_2dec(pack.cost) }}</td>
                                                        <td v-if="pack.experiment == 0" class="text-end py-2 border-bottom">
                                                            <span v-if="actions.loading_packages"><i class="fas fa-spinner fa-spin"></i></span>
                                                            <span v-else>{{ number_na(pack.num_constituents) }}</span>
                                                        </td>
                                                        <td v-if="pack.experiment == 0" class="text-end pe-3 py-2 border-bottom">
                                                            <span v-if="actions.loading_packages"><i class="fas fa-spinner fa-spin"></i></span>
                                                            <span v-else>{{ currency_2dec(packageCost(pack.cost, pack.num_constituents)) }}</span>
                                                        </td>
                                                    </tr>
                                                    <tr v-for="(pack, index) in packages">
                                                        <td v-if="pack.experiment == 1" class=" ps-3 py-2 border-bottom">{{ pack.appeal_code }}</td>
                                                        <td v-if="pack.experiment == 1" class="py-2 border-bottom">{{ pack.letter }}</td>
                                                        <td v-if="pack.experiment == 1" class="py-2 border-bottom">{{ pack.description }}</td>
                                                        <td v-if="pack.experiment == 1" class="text-end py-2 border-bottom">{{ currency_2dec(pack.cost) }}</td>
                                                        <td v-if="pack.experiment == 1" class="text-end py-2 border-bottom">
                                                            <span v-if="actions.loading_packages"><i class="fas fa-spinner fa-spin"></i></span>
                                                            <span v-else>
                                                                {{ number_na(pack.num_constituents - (isNaN(pack.experiment_num_constituents)?0:pack.experiment_num_constituents)) }}
                                                            </span>
                                                        </td>
                                                        <td v-if="pack.experiment == 1" class="text-end pe-3 py-2 border-bottom">
                                                            <span v-if="actions.loading_packages"><i class="fas fa-spinner fa-spin"></i></span>
                                                            <span v-else>
                                                                {{ currency_2dec(packageCost(pack.cost, isNaN(pack.num_constituents)?0:pack.num_constituents - (isNaN(pack.experiment_num_constituents)?0:pack.experiment_num_constituents))) }}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                    <tr v-for="(pack, index) in packages">
                                                        <td v-if="pack.experiment == 1" class="ps-3 py-2 border-bottom">{{ pack.experiment_appeal_code }}</td>
                                                        <td v-if="pack.experiment == 1" class="py-2 border-bottom">{{ pack.experiment_letter }}</td>
                                                        <td v-if="pack.experiment == 1" class="py-2 border-bottom" >{{ (pack.experiment_description == null || pack.experiment_description == "" ? pack.description + " (Treatment)" : pack.experiment_description) }}</td>
                                                        <td v-if="pack.experiment == 1" class="text-end py-2 border-bottom">{{ (pack.experiment_cost > 0 ? currency_2dec(pack.experiment_cost) : currency_2dec(pack.cost)) }}</td>
                                                        <td v-if="pack.experiment == 1" class="text-end py-2 border-bottom">
                                                            <span v-if="actions.loading_packages"><i class="fas fa-spinner fa-spin"></i></span>
                                                            <span v-else>
                                                                {{ number_na(pack.experiment_num_constituents) }}
                                                            </span>
                                                        </td>
                                                        <td v-if="pack.experiment == 1" class="text-end pe-3 py-2 border-bottom">
                                                            <span v-if="actions.loading_packages"><i class="fas fa-spinner fa-spin"></i></span>
                                                            <span v-else>
                                                                {{ currency_2dec(packageCost(pack.experiment_cost, pack.experiment_num_constituents)) }}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                </tbody>
                                                <tfoot>
                                                    <tr>
                                                        <td colspan="5" class="text-end"><strong>Fixed Costs:</strong></td>
                                                        <td class="text-end pe-3">
                                                            <span v-if="actions.loading_packages"><i class="fas fa-spinner fa-spin"></i></span>
                                                            <strong v-else>{{ currency_2dec(local_appeal.fixed_project_cost) }}</strong>
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td colspan="4" class="text-end bg-secondary"><strong>Total:</strong></td>
                                                        <td class="text-end py-2 bg-secondary">
                                                            <span v-if="actions.loading_packages">
                                                                <i class="fas fa-spinner fa-spin"></i>
                                                            </span>
                                                            <strong v-else>{{ number_na(totalConstituents) }}</strong>
                                                        </td>
                                                        <td class="text-end py-2 pe-3 bg-secondary">
                                                            <span v-if="actions.loading_packages">
                                                                <i class="fas fa-spinner fa-spin"></i>
                                                            </span>
                                                            <strong v-else>{{ currency_2dec(totalCosts) }}</strong>
                                                        </td>
                                                    </tr>
                                                </tfoot>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
    
                                    <div class="row mt-3">
                                        <div class="col">
                                            <label class="form-label" for="local_appeal.file_layout_id">Export File Layout</label>
                                            <select class="form-select" id="local_appeal.file_layout_id" v-model="local_appeal.file_layout_id" :disabled="!canEdit('direct_mail')" aria-label="Export File Layout">
                                                <option value="0">Choose a layout...</option>
                                                <option v-for="layout in layouts" :value="layout.id">{{ layout.name }}</option>
                                            </select>
                                        </div>
                                    </div>
    
                                    <div class="row mt-3">
                                        <div class="col">
                                            <div class="form-check">
                                                <input class="form-check-input" type="checkbox" id="include_in_reporting" v-model="include_in_reporting" :disabled="!canEdit('direct_mail')" aria-label="Include In Reporting">
                                                <label class="form-check-label" for="include_in_reporting">
                                                    Include the exported data in Direct Mail and Matchback reporting
                                                    <img class="info-icon ms-1" src="/img/icons/avid-icons/info.svg" data-bs-toggle="tooltip" title="This will utilize the accounts exported and the appeal codes associated with the packages for reporting" alt="" />
                                                </label>
                                            </div>
                                        </div>
                                    </div>
    
                                    <div class="alert alert-danger mt-3" role="alert" v-if="local_appeal.budget < totalCosts">
                                        <strong>Warning!</strong> You are currently <strong>{{ currency(totalCosts - local_appeal.budget) }}</strong> above your desired budget.
                                    </div>
    
                                    <div class="alert alert-success mt-3" role="alert" v-if="file_url != '#' && canExport('direct_mail')">
                                        <p><strong>Success!</strong> Your file has been generated. You will also be emailed a link to your file. For security reasons, this link will only work for the next hour.</p>
                                        <a v-bind:href="file_url" class="btn btn-primary btn-big" >Download Your File</a>
                                    </div>
    
                                    <div class="mt-3">
                                        <div v-if="!canExport('direct_mail')" class="d-inline-block" data-bs-toggle="tooltip" title="You do not have privileges to complete this action">
                                            <button class="btn btn-primary" :disabled="true">
                                                <span>Generate File</span>
                                            </button>
                                        </div>
                                        <button v-else class="btn btn-primary" @click="generateFiles()" :disabled="local_appeal.file_layout_id == 0 || local_appeal.file_layout_id == null || actions.generating" v-if="file_url == '#'">
                                            <span v-if="actions.generating"><i class="fas fa-spinner fa-spin"></i> Generating File</span>
                                            <span v-else>Generate File</span>
                                        </button>
                                        <a class="btn btn-secondary ms-2" :href="'/direct_mail/' + client.url + '/appeals'" >
                                            <span v-if="file_url != '#'">Finished</span>
                                            <span v-else>Cancel</span>
                                        </a>
                                    </div>                                
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <DescribeSegmentModal
            ref="describeSegmentModalRef"
            :client="client"
            modal-id="appeal-edit-describe-segment-modal"
            @submitted="onSegmentSuggestionSubmitted"
            @error="onSegmentSuggestionError"
        />
        <SegmentProposalModal
            ref="segmentProposalModalRef"
            :client="client"
            @saved="onSegmentProposalSaved"
        />
    </div>
    </template>
    
    <script>
        import { defineComponent } from 'vue';
        import draggable from 'vuedraggable';
        import PackageModal from './PackageModal.vue';
        import DescribeSegmentModal from '../pathways/DescribeSegmentModal.vue';
        import SegmentProposalModal from '../pathways/SegmentProposalModal.vue';
        import tablePaginationMixin from '../../mixins/table_pagination';
        import permissionsMixin from '../../mixins/permissions';
        import { Tooltip } from 'bootstrap';
        import { useNotifications } from '../../composables/useNotifications';
        import { useAISuggestionPolling, createSegmentSuggestionCheckFunction } from '../../composables/useAISuggestionPolling';

        export default defineComponent({
            name: 'AppealEdit',
            mixins: [tablePaginationMixin, permissionsMixin],
            components: {
                draggable,
                PackageModal,
                DescribeSegmentModal,
                SegmentProposalModal,
            },
            setup() {
                const { showNotification } = useNotifications();
                const { startPolling, stopPolling } = useAISuggestionPolling();
                return { showNotification, startPolling, stopPolling };
            },
            props: {
                client: {
                    type: Object,
                    required: true
                },
                appeal: {
                    type: Object,
                    required: true
                },
                layouts: {
                    type: Array,
                    required: true
                },
                segments: {
                    type: Array,
                    required: true
                },
                data_blend: {
                    type: Object,
                    required: true
                }
            },
            data() {
                return {
                    active_step: 1,
                    local_appeal: {
                        x_axis: '',
                        y_axis: '',
                        z_axis: '',
                        name: '',
                        drop_date: null,
                        appeal_code: '',
                        finder_number: '',
                        budget: '',
                        fixed_project_cost: '',
                        dedupe_priority: null,
                        one_record_per_household: false,
                        primary_addresses_only: false,
                        exclude_deceased: true,
                        file_layout_id: 0
                    },
                    steps: [
                        { title: 'Information', completed: false },
                        { title: 'Audience', completed: false },
                        { title: 'Packages', completed: false },
                        { title: 'Selection', completed: false },
                        { title: 'Review', completed: false }
                    ],
    
                    actions: {
                        loading: true,
                        default: true,
                        adding: false,
                        editing: false,
                        saving: false,
                        generating: false,
                        loading_packages: false
                    },
    
                    /* Package search and sort */
                    package_search: '',
                    package_sort_key: 'letter',
                    package_sort_order: 'asc',
                    filtered_packages: [],
    
                    /* Segment search */
                    segment_search: '',
                    filtered_segments: [],
                    segmentDragInProgress: false,
                    activeSegmentTab: 'targets',
    
                    axis_list: [],
    
                    /* step 2 */
                    active_segments: { // this is the list of stuff for the user on the front end
                        targets: [],
                        exclusions: [],
                        seeds: []
                    },
    
                    inactive_segments: [],
    
                    /* step 3 */
                    packages: [],
    
                    package: {
                        id: 0, // ID 0 means its a new package
                        direct_mail_id: 0,
                        description: '',
                        letter: 'A',
                        appeal_code: '',
                        carrier_postage: '',
                        reply_postage: '',
                        reply_card_version: 'A',
                        carrier_size: '',
                        letter_version: 'A',
                        double_sided: false,
                        color: '',
                        cost: 0,
                        experiment: false,
                        experiment_description: '',
                        experiment_letter: '',
                        experiment_appeal_code: '',
                        experiment_carrier_postage: '',
                        experiment_reply_postage: '',
                        experiment_reply_card_version: '',
                        experiment_carrier_size: '',
                        experiment_letter_version: '',
                        experiment_double_sided: '',
                        experiment_cost: 0
                    },
    
                    /* step 4 */
                    audiences: [],
                    cube: [],
                    file_url: '#',
                    file_dedupe: false,
                    include_in_reporting: false,
    
                    errors: {
                        name: '',
                        x_axis: '',
                        y_axis: '',
                        z_axis: '',
                        drop_date: ''
                    },
    
                    showPackageModal: false,
                    available_segments: [],
                    num_per_page: 5,
                    num_per_page_options: [5,10,15,20,25,30,50,100],
                    page_count: null,
                    page_index: 0,
                    page_index_array: [],
                    item_interval: '',
                    showing_description_for: null,
                };
            },
            beforeMount() {
                this.local_appeal = {
                    ...this.appeal,
                    drop_date: this.appeal.drop_date ? new Date(this.appeal.drop_date) : null,
                    file_id: this.appeal.file_id || null
                };
                this.inactive_segments = [...this.segments];
                this.filtered_segments = [...this.segments];
                this.available_segments = [...this.segments];
                this.loadDatabaseColumns();
                if(this.appeal.id != null && this.appeal.id > 0) {
                    this.setupUI();
                }
                else {
                    this.inactive_segments = [...this.segments];
                }
                this.reloadPagination();
    
                // Check for step parameter in URL
                const urlParams = new URLSearchParams(window.location.search);
                const stepParam = urlParams.get('step');
                if (stepParam && this.canNavigateToStep(parseInt(stepParam))) {
                    this.active_step = parseInt(stepParam);
                }
            },
            mounted() {
                // Wait for next tick to ensure DOM is ready
                this.$nextTick(() => {
                    try {
                        // Initialize tooltips
                        this.initializeTooltips();
    
                        // Initialize search after everything is set up
                        this.handleSegmentSearch();
                    } catch (error) {
                        console.error('Error in mounted hook:', error);
                    }
                });
            },
            beforeUnmount() {
            },
            computed: {
                progressPercentage() {
                    return (this.active_step / (this.steps.length)) * 100;
                },
                segmentTabOptions() {
                    return [
                        { key: 'targets', label: 'Inclusions' },
                        { key: 'exclusions', label: 'Exclusions' },
                        { key: 'seeds', label: 'Seeds' }
                    ];
                },
                segmentTabLabel() {
                    const tab = this.segmentTabOptions.find(t => t.key === this.activeSegmentTab);
                    return tab ? tab.label : '';
                },
                segmentTabDescription() {
                    const descriptions = {
                        targets: 'Who should be included in the appeal?',
                        exclusions: 'Who should be suppressed from the Includes audiences?',
                        seeds: 'Who should get this appeal regardless of the Includes and Exclusions settings?'
                    };
                    return descriptions[this.activeSegmentTab] || '';
                },
                totalConstituents() {
                    if (this.actions.loading_packages) {
                        return 0;
                    }
                    var total = 0;
                    for (var i = 0; i < this.packages.length; i++) {
                        var constits = (this.packages[i].num_constituents === undefined) ? 0 : this.packages[i].num_constituents;
                        total += parseInt(constits);
                    }
                    return total;
                },
                totalCosts() {
                    if (this.actions.loading_packages) {
                        return 0;
                    }
                    var total_costs = 0;
                    for (var i = 0; i < this.packages.length; i++) {
                        var constits = (this.packages[i].num_constituents === undefined) ? 0 : this.packages[i].num_constituents;
    
                        if(this.packages[i].experiment == 1) {
                            var exp_constits = (this.packages[i].experiment_num_constituents === undefined) ? 0 : this.packages[i].experiment_num_constituents;
    
                            total_costs += (parseInt(constits)-parseInt(exp_constits)) * parseFloat(this.packages[i].cost);
                            total_costs += (parseInt(exp_constits) * parseFloat(this.packages[i].experiment_cost));
                        }
                        else
                            total_costs += (parseInt(constits) * parseFloat(this.packages[i].cost));
                    }
                    return parseFloat(this.local_appeal.fixed_project_cost) + total_costs;
                }
            },
            methods: {
                openDescribeSegmentModal() {
                    this.$refs.describeSegmentModalRef?.show();
                },
                onSegmentSuggestionSubmitted({ segment_suggestion_id }) {
                    this.showNotification({
                        icon: '/img/icons/nav-icons/Edna.svg',
                        text: "We're creating your audience. You'll be notified when it's ready!",
                        type: 'info',
                        noDismiss: true,
                    });
                    const requestKey = `segment-suggestion-${segment_suggestion_id}`;
                    this.startPolling({
                        requestKey,
                        checkFn: createSegmentSuggestionCheckFunction({
                            clientUrl: this.client.url,
                            segmentSuggestionId: segment_suggestion_id,
                        }),
                        onSuccess: (data) => {
                            this.showNotification({
                                icon: '/img/icons/nav-icons/Edna.svg',
                                text: "Your audience suggestion is ready. Click to view.",
                                type: 'success',
                                action: { type: 'openSegmentProposal', segment_suggestion_id: data.id },
                            });
                        },
                        onTimeout: () => {},
                    });
                },
                onSegmentSuggestionError(message) {
                    this.showNotification({ text: message || 'Failed to submit.', type: 'error' });
                },
                onSegmentProposalSaved({ segment }) {
                    if (segment && this.inactive_segments) {
                        if (!this.inactive_segments.some(s => s.id === segment.id)) {
                            this.inactive_segments.push(segment);
                            this.inactive_segments.sort((a, b) => a.name.localeCompare(b.name));
                        }
                        this.handleSegmentSearch();
                    }
                },
                loadDatabaseColumns() {
                    let data = {
                        client: this.client,
                        include_trans: false,
                        source: 'data_blend',
                        data_source_id: this.data_blend.id,
                    };
                    window.axios.post('/api/bigquery/get_db_columns', data)
                    .then(response => {
                        // Initialize with default axes first
                        this.axis_list = [
                            { value: '`avid-ai.udf.GetRevenueRange`(giving_history.first_gift.amount)', name: 'First Gift Amount' },
                            { value: '`avid-ai.udf.GetRevenueRange`(giving_history.last_gift.amount)', name: 'Last Gift Amount' },
                            { value: '`avid-ai.udf.GetRevenueRange`(giving_history.largest_gift.amount)', name: 'Highest Gift Amount' },
                            { value: '`avid-ai.udf.GetRevenueRange`(giving_summary.average_gift)', name: 'Average Gift Amount' },
                            { value: '`avid-ai.udf.GetRevenueRange`(giving_summary.largest_given_past_24_months)', name: 'Largest Gift in Last 24 Months Amount' },
                            { value: '`avid-ai.udf.GetGiftsRange`(giving_summary.lifetime_gifts)', name: 'Lifetime Gifts' },
                            { value: '`avid-ai.udf.GetRevenueRange`(giving_summary.lifetime_revenue)', name: 'Lifetime Revenue' },
                            { value: '`avid-ai.udf.Get12MonthsSince`(giving_history.last_gift.date)', name: 'Months Since Last Gift' },
                            { value: '`avid-ai.udf.GetMonthsSince`(giving_history.last_gift.date)', name: 'Months Since Last Gift (Expanded)' },
                            { value: 'giving_summary.current_fy_lifecycle', name: 'Fiscal Year Lifecycle' },
                            { value: 'giving_summary.current_trailing_12_months_lifecycle', name: 'Trailing 12 Mo. Lifecycle' }
                        ];
    
                        // Add custom calculations: use actual column type from API to apply UDF (no guessing)
                        for(var i = 0; i < response.data.columns.length; i++) {
                            if(response.data.columns[i].value.startsWith('custom_calculation')) {
                                let col = response.data.columns[i];
                                let newAxis = {
                                    value: col.value,
                                    name: col.text
                                };
                                const colType = (col.type || '').toLowerCase();

                                if (colType === 'float' || colType === 'int') {
                                    // Numeric: choose range UDF by semantic hint (revenue vs count)
                                    if (col.value.includes('gifts') || col.value.includes('num') || colType === 'int') {
                                        newAxis.value = '`avid-ai.udf.GetGiftsRange`(' + col.value + ')';
                                    } else if (col.value.includes('revenue') || col.value.includes('amount') ||
                                        col.value.includes('amt') || col.value.includes('average')) {
                                        newAxis.value = '`avid-ai.udf.GetRevenueRange`(' + col.value + ')';
                                    } 
                                } else if (colType === 'date') {
                                    newAxis.value = '`avid-ai.udf.GetMonthsSince`(' + col.value + ')';
                                }
                                // else: leave raw value (string, etc.)

                                this.axis_list.push(newAxis);
                            }
                        }
    
                        // Sort the axis_list by name
                        this.axis_list.sort((a, b) => a.name.localeCompare(b.name));
                        this.updateAxisList();
                    });
                },
                setupUI() {
                    //Copy the appeal.packages array to the packages array
                    this.packages = [...this.appeal.packages];
                    this.audiences = [...this.appeal.audiences];
                    this.filtered_packages = [...this.packages];
                    for(var i = 0; i < this.appeal.segments.length; i++) {
                        if(this.appeal.segments[i].segment) {
                            this.active_segments[this.appeal.segments[i].type].push(this.appeal.segments[i].segment);
                        }
                    }
                    this.inactive_segments = [...this.segments];
                    //Sort the audiences by the audience.name
                    this.inactive_segments.sort((a, b) => a.name.localeCompare(b.name));
                    //loop through the active segment types (object keys) (targets, exclusions, seeds)
                    for(var i = 0; i < Object.keys(this.active_segments).length; i++) {
                        //loop through the segments in the active segment type
                        for(var j = 0; j < this.active_segments[Object.keys(this.active_segments)[i]].length; j++) {
                            //remove the segment from the inactive segments
                            this.inactive_segments = this.inactive_segments.filter(segment => segment.id !== this.active_segments[Object.keys(this.active_segments)[i]][j].id);
                        }
                    }
                    this.getCube();
                    if(this.appeal.dedupe_priority != null) {
                        this.file_dedupe = true;
                    }
    
                },
                getCube(){
                    axios.get('/api/direct_mail/cube/'+ this.local_appeal.id).then((res) => {
                        this.cube = res.data.cube;
                    });
                },
                getSize(segment) {
                    //Find the segment.sizes array that has the segment.id = 0
                    for (var i = 0; i < segment.sizes.length; i++) {
                        if (segment.sizes[i].id == 0) {
                            return segment.sizes[i].size;
                        }
                    }
                    return 0;
                },
                stripNonNumbers(e) {
                    // Prevent the default pasting event and stop bubbling
                    e.preventDefault()
                    e.stopPropagation()
                    // Get the clipboard data
                    var paste = (e.clipboardData || window.clipboardData).getData('text/plain')
    
                    paste = paste.replace(/[^\d.-]/g, '');
    
                    if (document.queryCommandSupported("insertText")) {
                        document.execCommand("insertText", false, paste);
                    } else {
                        document.execCommand("paste", false, paste);
                    }
                },
                isNumber: function(evt) {
                    evt = (evt) ? evt : window.event;
                    var charCode = (evt.which) ? evt.which : evt.keyCode;
                    if ((charCode > 31 && (charCode < 48 || charCode > 57)) && charCode !== 46 && charCode !== 44) {
                        evt.preventDefault();
                    } else {
                        return true;
                    }
                },
                canNavigateToStep(step) {
                    // If appeal has an ID, all steps are accessible
                    if (this.local_appeal.id != null && this.local_appeal.id !== 0) {
                        if(step == 3 && this.active_segments.targets.length == 0) {
                            return false;
                        }
                        if(step >= 4 && this.packages.length == 0) {
                            return false;
                        }
                        return true;
                    }
    
                    // Can always go back to previous steps
                    if (step <= this.active_step) {
                        return true;
                    }
    
                    // Can only proceed to next step if current step is completed
                    return step === this.active_step + 1 && this.steps[this.active_step - 1].completed;
                },
                goToStep(step) {
                    if (this.canNavigateToStep(step)) {
                        this.active_step = step;

                        // Update URL with current step
                        const url = new URL(window.location.href);
                        url.searchParams.set('step', step);
                        window.history.pushState({}, '', url);

                        if (step === 1) {
                            this.loadDatabaseColumns();
                        }
                        if (step === 5) {
                            this.loadFinalPackages();
                        }

                        // Initialize tooltips after step change
                        this.$nextTick(() => {
                            this.initializeTooltips();
                        });
                    }
                },
                nextStep() {
                    var next_step = this.active_step + 1;
                    if (next_step <= this.steps.length) {
                        this.steps[this.active_step-1].completed = true;
                        this.active_step = next_step;
    
                        // Update URL with new step
                        const url = new URL(window.location.href);
                        url.searchParams.set('step', next_step);
                        window.history.pushState({}, '', url);
    
                        if (this.active_step === 5) {
                            this.loadFinalPackages();
                        }
    
                        // Initialize tooltips after step change
                        this.$nextTick(() => {
                            this.initializeTooltips();
                        });
                    }
                },
    
                validateAppeal() {
                    let isValid = true;
                    this.errors.name = '';
                    this.errors.x_axis = '';
                    this.errors.y_axis = '';
                    this.errors.z_axis = '';
                    this.errors.drop_date = '';
    
                    if (!this.local_appeal.name) {
                        this.errors.name = 'Please choose a name for the appeal.';
                        isValid = false;
                    }
    
                    if (!this.local_appeal.drop_date) {
                        this.errors.drop_date = 'Please select a drop date.';
                        isValid = false;
                    }
    
                    if (!this.local_appeal.x_axis) {
                        this.errors.x_axis = 'Please select an X axis.';
                        isValid = false;
                    }
    
                    if (!this.local_appeal.y_axis) {
                        this.errors.y_axis = 'Please select a Y axis.';
                        isValid = false;
                    }
    
                    if (!this.local_appeal.z_axis) {
                        this.errors.z_axis = 'Please select a Z axis.';
                        isValid = false;
                    }
    
                    return isValid;
                },
    
                saveAppeal(nextStep = false) {
                    if (!this.validateAppeal()) {
                        return;
                    }
    
                    // Check permissions before saving
                    if (!this.canEdit('direct_mail')) {
                        // If no permissions, just proceed to next step without saving
                        if(nextStep) {
                            this.nextStep();
                        }
                        return;
                    }
    
                    this.actions.saving = true;
    
                    // Create a copy of local_appeal with the date converted to string format
                    const appealData = {
                        ...this.local_appeal,
                        drop_date: this.local_appeal.drop_date ? this.local_appeal.drop_date.toISOString().split('T')[0] : null
                    };
    
                    if (this.local_appeal.id == null || this.local_appeal.id == 0) { // Create a new appeal
                        axios.post('/api/direct_mail', appealData).then((res) => {
                            this.local_appeal = {
                                ...res.data.directMail,
                                drop_date: res.data.directMail.drop_date ? new Date(res.data.directMail.drop_date) : null
                            };
                            this.actions.saving = false;
                            this.package.direct_mail_id = res.data.directMail.id;
                            
                            // Update the URL to reflect the new appeal ID
                            const newUrl = `/direct_mail/${this.client.url}/appeals/edit/${res.data.directMail.id}`;
                            window.history.pushState({}, '', newUrl);
                        }).catch((error) => {
                            this.actions.saving = false;
                        });
                    } else { // Update the appeal
                        axios.put('/api/direct_mail/'+ this.local_appeal.id, appealData).then((res) => {
                            this.cube = res.data.cube;
                            this.actions.saving = false;
                            this.resetPackage();
                        }).catch((error) => {
                            this.actions.saving = false;
                        });
                    }
    
                    if(nextStep) {
                        this.nextStep();
                    }
                },
    
                saveAppealSegments() {
                    // Check permissions before saving
                    if (!this.canEdit('direct_mail')) {
                        // If no permissions, just proceed to next step without saving
                        this.nextStep();
                        return;
                    }
    
                    this.actions.saving = true;
                    axios.post('/api/direct_mail/segment', {direct_mail_id: this.local_appeal.id, segments: this.active_segments}).then((res) => {
                        this.cube = res.data.cube;
                        this.actions.saving = false;
                    }).catch((error) => {
                        this.actions.saving = false;
                    });
                    this.nextStep();
    
                },
    
                updateAxisList() {
    
                    this.axis_list.forEach((axis) => {
                        axis.selected = (this.local_appeal.x_axis == axis.value ||
                            this.local_appeal.y_axis == axis.value ||
                            this.local_appeal.z_axis == axis.value) ? true : false;
                    });
                },
    
                editPackage(index) {
                    if(this.packages[index].cost == null)
                        this.packages[index].cost = 0;
                    if(this.packages[index].experiment_cost == null)
                        this.packages[index].experiment_cost = 0;
    
                    this.package = JSON.parse(JSON.stringify(this.packages[index]));
                    this.openPackageModal('edit');
                },
    
                resetPackage() {
                    var letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
                    this.actions.saving = false;
                    this.actions.adding = false;
                    this.actions.editing = false;
    
                    this.package = {
                        id: 0, // ID 0 means its a new package
                        direct_mail_id: this.local_appeal.id,
                        description: '',
                        letter: letters.charAt(this.packages.length),
                        appeal_code: this.local_appeal.appeal_code,
                        carrier_postage: '',
                        reply_postage: '',
                        reply_card_version: letters.charAt(this.packages.length),
                        carrier_size: '',
                        letter_version: letters.charAt(this.packages.length),
                        double_sided: false,
                        color: '',
                        cost: 0,
                        experiment: false,
                        experiment_description: '',
                        experiment_letter: '',
                        experiment_appeal_code: '',
                        experiment_carrier_postage: '',
                        experiment_reply_postage: '',
                        experiment_reply_card_version: '',
                        experiment_carrier_size: '',
                        experiment_letter_version: '',
                        experiment_double_sided: '',
                        experiment_cost: 0
                    };
                },
    
                validatePackage() {
                    let isValid = true;
                    this.errors.package_letter = '';
                    this.errors.package_description = '';
                    this.errors.package_color = '';
    
                    if (!this.package.letter) {
                        this.errors.package_letter = 'Package code is required.';
                        isValid = false;
                    }
    
                    if (!this.package.description) {
                        this.errors.package_description = 'Please add a package description.';
                        isValid = false;
                    }
    
                    if (!this.package.color) {
                        this.errors.package_color = 'Please choose a color.';
                        isValid = false;
                    }
    
                    return isValid;
                },
    
                savePackage() {
                    if (!this.validatePackage()) {
                        return;
                    }
    
                    // Check permissions before saving
                    if (!this.canEdit('direct_mail')) {
                        Swal.fire({
                            title: 'Permission Denied',
                            text: 'You do not have permission to save packages for direct mail appeals.',
                            icon: "warning",
                            customClass: {
                                popup: 'swal-warning'
                            },
                        });
                        return;
                    }
    
                    this.actions.saving = true;
                    this.package.direct_mail_id = this.local_appeal.id;
    
                    if (this.package.id == 0) { // Create a new package
                        axios.post('/api/direct_mail/package', this.package).then(response => {
                            this.packages.push(response.data.package);
                            this.filtered_packages = [...this.packages];
                            this.actions.saving = false;
                            this.closePackageModal();
                        }).catch(error => {
                            this.actions.saving = false;
                            console.error('Error saving package:', error);
                        });
                    } else { // Update the package
                        const index = this.packages.findIndex(p => p.id === this.package.id);
                        if (index !== -1) {
                            axios.put(`/api/direct_mail/package/${this.package.id}`, this.package).then(response => {
                                this.packages.splice(index, 1, response.data.package);
                                this.filtered_packages = [...this.packages];
                                this.actions.saving = false;
                                this.closePackageModal();
                            }).catch(error => {
                                this.actions.saving = false;
                                console.error('Error updating package:', error);
                            });
                        }
                    }
                },
    
                removePackage(i) {
                    // Check permissions before removing
                    if (!this.canEdit('direct_mail')) {
                        Swal.fire({
                            title: 'Permission Denied',
                            text: 'You do not have permission to remove packages from direct mail appeals.',
                            icon: "warning",
                            customClass: {
                                popup: 'swal-warning'
                            },
                        });
                        return;
                    }
    
                    var letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    
                    axios.delete('/api/direct_mail/package/' + this.packages[i].id).then((res) => {
                        this.packages.splice(i, 1);
                    });
                },
    
                packageCost(cost, size) {
                    if (size === undefined)
                        size = 0;
                    return parseFloat(cost) * parseInt(size);
                },
    
                generateFiles() {
                    // Check permissions before generating files
                    if (!this.canExport('direct_mail')) {
                        Swal.fire({
                            title: 'Permission Denied',
                            text: 'You do not have permission to generate files for direct mail appeals.',
                            icon: "warning",
                            customClass: {
                                popup: 'swal-warning'
                            },
                        });
                        return;
                    }
    
                    // Check if there's an existing file_id and user is checking the reporting checkbox
                    if (this.include_in_reporting && this.local_appeal.file_id) {
                        Swal.fire({
                            title: 'Warning',
                            text: 'Your previous reporting export will be overwritten. Do you want to continue?',
                            icon: "warning",
                            showCancelButton: true,
                            confirmButtonText: 'Yes, continue',
                            cancelButtonText: 'Cancel',
                            customClass: {
                                popup: 'swal-warning'
                            },
                        }).then((result) => {
                            if (result.isConfirmed) {
                                this.processFileGeneration();
                            }
                        });
                        return;
                    }
    
                    this.processFileGeneration();
                },
                processFileGeneration() {
                    this.actions.generating = true;
    
                    axios.put('/api/direct_mail/'+ this.local_appeal.id, this.local_appeal).then((res) => {
                        const params = new URLSearchParams();
                        if (this.include_in_reporting) {
                            params.append('include_in_reporting', '1');
                        }
                        const url = '/api/direct_mail/process/'+ this.local_appeal.id + (params.toString() ? '?' + params.toString() : '');
                        axios.get(url)
                            .then((res) => {
                                this.file_url = res.data.url;
                                // Update local_appeal with any returned data (like file_id)
                                if (res.data.direct_mail) {
                                    this.local_appeal = {
                                        ...this.local_appeal,
                                        ...res.data.direct_mail
                                    };
                                }
                                this.actions.generating = false;
                            })
                            .catch((error) => {
                                this.actions.generating = false;
    
                                Swal.fire({
                                    title: 'Error!',
                                    text: error.response?.data?.message || 'An error occurred while processing the file',
                                    icon: "error",
                                    customClass: {
                                        popup: 'swal-error'
                                    },
                                });
                            });
                    });
                },
    
                getFileUrl() {
                    axios.post('/api/direct_mail/get_url' , {url: this.appeal.exported_url, client_id: this.client.id}).then(function(res) {
                        if (res.data.success) {
                            var link = document.createElement("a");
                                //link.download = self.local_appeal.name ".csv";
                            link.href = res.data.url;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                        }
                    });
                },
    
                toggleDedupe() {
                    if (this.file_dedupe) {
                        this.local_appeal.dedupe_priority = 'giving_history.largest_gift.amount';
                    } else {
                        this.local_appeal.dedupe_priority = null;
                    }
    
                },
    
                handlePackageSearch() {
                    const searchTerm = this.package_search.toLowerCase();
                    this.filtered_packages = this.packages.filter(pack => {
                        return pack.letter.toLowerCase().includes(searchTerm) ||
                                pack.description.toLowerCase().includes(searchTerm) ||
                                this.postage(pack.carrier_postage).toLowerCase().includes(searchTerm) ||
                                this.postage(pack.reply_postage).toLowerCase().includes(searchTerm) ||
                                this.size(pack.carrier_size).toLowerCase().includes(searchTerm) ||
                                this.readable_boolean(pack.double_sided).toLowerCase().includes(searchTerm)
                    });
                },
    
                sortPackagesByKey(key) {
                    if (this.package_sort_key === key) {
                        this.package_sort_order = this.package_sort_order === 'asc' ? 'des' : 'asc';
                    } else {
                        this.package_sort_key = key;
                        this.package_sort_order = 'asc';
                    }
    
                    this.filtered_packages.sort((a, b) => {
                        let aVal = a[key];
                        let bVal = b[key];
    
                        // Apply filters for display values if needed
                        if (key === 'carrier_postage' || key === 'reply_postage') {
                            aVal = this.postage(aVal);
                            bVal = this.postage(bVal);
                        } else if (key === 'carrier_size') {
                            aVal = this.size(aVal);
                            bVal = this.size(bVal);
                        } else if (key === 'double_sided') {
                            aVal = this.readable_boolean(aVal);
                            bVal = this.readable_boolean(bVal);
                        }
    
                        if (this.package_sort_order === 'asc') {
                            return (aVal > bVal) ? 1 : -1;
                        } else {
                            return (aVal < bVal) ? 1 : -1;
                        }
                    });
                },
    
                openPackageModal(mode) {
                    if (mode === 'add') {
                        this.resetPackage();
                    }
                    this.showPackageModal = true;
                },
                closePackageModal() {
                    this.showPackageModal = false;
                    this.resetPackage();
                },
                handlePackageSaved(pkg) {
                    this.packages.push(pkg);
                    this.filtered_packages = [...this.packages];
                },
                handlePackageUpdated(pkg) {
                    const index = this.packages.findIndex(p => p.id === pkg.id);
                    if (index !== -1) {
                        this.packages.splice(index, 1, pkg);
                        this.filtered_packages = [...this.packages];
                    }
                },
                handleSegmentSearch() {
                    // Ensure arrays are properly initialized
                    if (!this.inactive_segments) {
                        this.inactive_segments = [...this.segments];
                    }
                    if (!this.filtered_segments) {
                        this.filtered_segments = [...this.segments];
                    }
                    
                    const searchTerm = this.segment_search.toLowerCase();
                    this.filtered_segments = this.inactive_segments.filter(segment => {
                        return segment.name.toLowerCase().includes(searchTerm);
                    });
                    
                    // Update available segments to match filtered segments
                    this.available_segments = [...this.filtered_segments];
                    
                    // Update pagination only if filtered_segments is properly initialized
                    if (this.filtered_segments && Array.isArray(this.filtered_segments)) {
                        this.reloadPagination();
                        this.moveToPage(1);
                    }
                },
                handleDragChange(evt) {
                    /*
                     * This handler is bound to the draggable list that represents the **available** segments.
                     * We need to update our internal tracking lists (`inactive_segments`, `filtered_segments`)
                     * whenever an element is either removed from this list (dragged into an active bucket)
                     * or added back (dragged from an active bucket).
                     */
                    if (evt.removed) {
                        // A segment was dragged **out** of the available list → remove it from `inactive_segments`.
                        const segment = evt.removed.element;
                        const idx = this.inactive_segments.findIndex(s => s.id === segment.id);
                        if (idx !== -1) {
                            this.inactive_segments.splice(idx, 1);
                        }
    
                        // Also remove it from any filtered view and refresh pagination.
                        this.filtered_segments = this.filtered_segments.filter(s => s.id !== segment.id);
                        this.reloadPagination();
                    }
    
                    if (evt.added) {
                        // A segment was dragged **into** the available list → add it back to `inactive_segments`.
                        const segment = evt.added.element;
                        if (!this.inactive_segments.some(s => s.id === segment.id)) {
                            this.inactive_segments.push(segment);
                            // Keep the list alphabetically sorted for a better UX.
                            this.inactive_segments.sort((a, b) => a.name.localeCompare(b.name));
                        }
    
                        // Re-run the current search so the item shows up immediately if it matches.
                        this.handleSegmentSearch();
                    }
                },
                onAvailableSegmentDragStart(evt) {
                    this.segmentDragInProgress = true;
                    if (evt && evt.item) {
                        evt.item.classList.add('appeal-drag-preview');
                    }
                },
                onAvailableSegmentDragEnd(evt) {
                    this.segmentDragInProgress = false;
                    if (evt && evt.item) {
                        evt.item.classList.remove('appeal-drag-preview');
                    }
                },
                handleActiveSegmentDragChange(evt) {
                    /*
                     * This handler is bound to each draggable list that represents an **active** bucket
                     * (targets, exclusions, seeds). The logic is the inverse of `handleDragChange`.
                     */
                    if (evt.added) {
                        // A segment was dragged **into** an active bucket → remove it from `inactive_segments`.
                        const segment = evt.added.element;
                        const idx = this.inactive_segments.findIndex(s => s.id === segment.id);
                        if (idx !== -1) {
                            this.inactive_segments.splice(idx, 1);
                        }
                        // Ensure it disappears from any current search view.
                        this.filtered_segments = this.filtered_segments.filter(s => s.id !== segment.id);
                        this.reloadPagination();
                    }
    
                    if (evt.removed) {
                        // A segment was dragged **out** of an active bucket back to the available list.
                        const segment = evt.removed.element;
                        if (!this.inactive_segments.some(s => s.id === segment.id)) {
                            this.inactive_segments.push(segment);
                            this.inactive_segments.sort((a, b) => a.name.localeCompare(b.name));
                        }
                        // Refresh search results to include this item if it matches the current term.
                        this.handleSegmentSearch();
                    }
                },
                getTextColorClass(backgroundColor) {
                    // Remove the # if it exists
                    let hex = backgroundColor.replace('#', '');
    
                    // Convert to standard 6-character format if in 3-character shorthand
                    if (hex.length === 3) {
                        hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
                    }
    
                    // Convert to RGB
                    const r = parseInt(hex.substring(0, 2), 16);
                    const g = parseInt(hex.substring(2, 4), 16);
                    const b = parseInt(hex.substring(4, 6), 16);
    
                    // Calculate perceived brightness using the YIQ formula
                    const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
    
                    // Return black for light backgrounds, white for dark backgrounds
                    return yiq >= 128 ? 'text-black' : 'text-white';
                },
                loadFinalPackages() {
                    this.actions.loading_packages = true;
                    axios.get('/api/direct_mail/package/' + this.local_appeal.id)
                        .then(response => {
                            this.packages = response.data.packages;
                            this.filtered_packages = [...this.packages];
                            this.actions.loading_packages = false;
                        })
                        .catch(error => {
                            console.error('Error loading packages:', error);
                            this.actions.loading_packages = false;
                        });
                },
                addSegmentToFirstAvailable(segment) {
                    this.active_segments[this.activeSegmentTab].push(segment);
                    const idx = this.available_segments.findIndex(s => s.id === segment.id);
                    if (idx !== -1) this.available_segments.splice(idx, 1);
                    const inactiveIdx = this.inactive_segments.findIndex(s => s.id === segment.id);
                    if (inactiveIdx !== -1) this.inactive_segments.splice(inactiveIdx, 1);
                    this.filtered_segments = this.filtered_segments.filter(s => s.id !== segment.id);
                    this.reloadPagination();
                },
                removeSegment(segment, type) {
                    // Remove from active segments
                    const index = this.active_segments[type].findIndex(s => s.id === segment.id);
                    if (index !== -1) {
                        this.active_segments[type].splice(index, 1);
                    }
                    
                    // Add back to inactive segments if not already there
                    if (!this.inactive_segments.some(s => s.id === segment.id)) {
                        this.inactive_segments.push(segment);
                        // Sort the inactive segments by name
                        this.inactive_segments.sort((a, b) => a.name.localeCompare(b.name));
                    }
    
                    // Update filtered segments with the current search term
                    this.handleSegmentSearch();
                },
                copyPackage(index) {
                    // Create a deep copy of the package
                    const packageCopy = JSON.parse(JSON.stringify(this.packages[index]));
                    
                    // Reset the ID to 0 to indicate it's a new package
                    packageCopy.id = 0;
                    
                    // Append " (Copy)" to the description
                    packageCopy.description = packageCopy.description + " (Copy)";
                    
                    // Set this as the current package and open the modal
                    this.package = packageCopy;
                    this.openPackageModal('edit');
                },
                showSegmentDescription(segment) {
                    this.showing_description_for = this.showing_description_for === segment.id ? null : segment.id;
                },
                isAxisSelected(axisValue) {
                    return this.local_appeal.x_axis === axisValue || 
                            this.local_appeal.y_axis === axisValue || 
                            this.local_appeal.z_axis === axisValue;
                },
                reloadPagination() {
                    // Ensure filtered_segments is properly initialized
                    if (!this.filtered_segments || !Array.isArray(this.filtered_segments)) {
                        this.filtered_segments = [];
                    }
                    
                    this.page_count = Math.ceil(this.filtered_segments.length / this.num_per_page);
                    this.page_index_array = Array.from({length: this.page_count}, (_, i) => i + 1);
                    this.getItemInterval();
                },
                getItemInterval() {
                    // Ensure filtered_segments is properly initialized
                    if (!this.filtered_segments || !Array.isArray(this.filtered_segments)) {
                        this.filtered_segments = [];
                    }
                    
                    var from = this.page_index * this.num_per_page + 1;
                    var to = (this.page_index + 1) * this.num_per_page;
                    if (to > this.filtered_segments.length) {
                        to = this.filtered_segments.length;
                    }
                    if (from >= to) {
                        this.item_interval = to;
                    } else {
                        this.item_interval = from + ' - ' + to;
                    }
    
                    // Update the displayed segments based on pagination
                    this.available_segments = this.filtered_segments.slice(from - 1, to);
                },
                moveToPrevPage() {
                    if (this.page_index > 0) {
                        this.page_index--;
                        this.getItemInterval();
                    }
                },
                moveToPage(page) {
                    this.page_index = page - 1;
                    this.getItemInterval();
                },
                moveToNextPage() {
                    if (this.page_index < this.page_count - 1) {
                        this.page_index++;
                        this.getItemInterval();
                    }
                },
                postage(str) {
                    return (str == 'first_class') ? 'First-Class' :
                            (str == 'first_class_indicia') ? 'First-Class Indicia' :
                            (str == 'first_class_presort') ? 'First-Class Presort' :
                            (str == 'first_class_old_stamp') ? 'First-Class, Old Stamps' :
                            (str == 'non_profit') ? 'Non-Profit' :
                            (str == 'non_profit_indicia') ? 'Non-Profit Indicia' :
                            (str == 'non_profit_meter') ? 'Non-Profit Meter' :
                            (str == 'non_profit_big_stamp') ? 'Non-Profit, Big Stamp' :
                            (str == 'non_profit_two_stamps') ? 'Non-Profit, Two Stamps' :
                            (str == 'presorted_first_class') ? 'Presorted First-Class' :
                            (str == 'unstamped_rae') ? 'Unstamped RAE' :
                            (str == 'bre') ? 'BRE' :
                            (str == 'rae') ? 'RAE' :
                            (str == 'rae_first_class') ? 'RAE, First-Class' :
                            (str == 'none') ? 'None' : str;
                },
                size(size) {
                    return (size == '10') ? '#10' :
                            (size == '11') ? '#11' :
                            (size == '12') ? '#12' :
                            (size == '14') ? '#14' :
                            (size == '10_13') ? '10 x 13' :
                            (size == '11_6') ? '11 x 6' :
                            (size == '6_10') ? '6 x 10' :
                            (size == '6_11') ? '6 x 11' :
                            (size == '6_4') ? '6 x 4' :
                            (size == '6_9') ? '6 x 9' :
                            (size == '6_9.5') ? '6 x 9.5' :
                            (size == '9_12') ? '9 x 12' :
                            (size == '9_12_brownkraft') ? '9 x 12 Brownkraft' :
                            (size == 'executive') ? 'Executive' :
                            (size == 'self') ? 'Self' : size;
                },
                readable_boolean(bool) {
                    return (bool) ? 'Yes' : 'No';
                },
                initializeTooltips() {
                    const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
                    const tooltipList = [...tooltipTriggerList].map(tooltipTriggerEl => new window.bootstrap.Tooltip(tooltipTriggerEl));
    
                    for (var i = 0; i < tooltipTriggerList.length; i++) {
                        (function(index) {
                            tooltipTriggerList[index].addEventListener('click', function() {
                                tooltipList[index].hide();
                            });
                            tooltipTriggerList[index].addEventListener('mouseleave', function() {
                                tooltipList[index].hide();
                            });
                        })(i);
                    }
                }
            },
            filters: {
                reloadPagination() {
                    // Ensure filtered_segments is properly initialized
                    if (!this.filtered_segments || !Array.isArray(this.filtered_segments)) {
                        this.filtered_segments = [];
                    }
                    
                    this.page_count = Math.ceil(this.filtered_segments.length / this.num_per_page);
                    this.page_index_array = Array.from({length: this.page_count}, (_, i) => i + 1);
                    this.getItemInterval();
                },
            }
        });
    </script>